import React from "react";
import { CheckoutPaymentMethod } from "../components/CheckoutPaymentMethod";
import { CheckoutShipping } from "../components/CheckoutShipping";
import { CheckoutReview } from "../components/CheckoutReview";
import { CheckoutPayment } from "../components/CheckoutPayment";

interface StepProps {
  // Payment Method props
  paymentMethods?: any;
  isOnlinePaymentEnabled?: boolean;
  isEmailVerified?: boolean;
  selectedMethod?: any;
  onSelect?: (method: any) => void;
  onNext?: () => void;
  onBack?: () => void;
  loading?: boolean;
  countryCode?: string;
  isDigitalOnlyOrder?: boolean; // For disabling cash_on_pickup in digital orders
  isStripeConfigured?: boolean;
  isPayPalConfigured?: boolean;

  // Shipping props
  shippingAddress?: any;
  billingAddress?: any;
  useSameAddress?: boolean;
  shippingRates?: any[];
  selectedShipping?: string;
  onShippingAddressChange?: (address: any) => void;
  onBillingAddressChange?: (address: any) => void;
  onUseSameAddressChange?: (value: boolean) => void;
  onShippingSelect?: (id: string) => void;
  errors?: Record<string, string>;
  onShippingAddressSelect?: (addressId: string) => void;
  onBillingAddressSelect?: (addressId: string) => void;
  selectedShippingAddressId?: string | null;
  selectedBillingAddressId?: string | null;

  // Review props
  cartItems?: any[];
  subtotal?: number;
  tax?: number;
  shipping?: number;
  total?: number;
  currency?: string;
  onConfirm?: () => void;
  isOnlinePaymentEnabledReview?: boolean;
  notes?: string;
  onNotesChange?: (notes: string) => void;
  getSelectedShippingMethod?: () => any;

  // Payment props
  clientSecret?: string | null;
  amount?: number;
  stripePublishableKey?: string | null;
  paypalClientId?: string | null;
  selectedGateway?: "stripe" | "paypal" | null;
  onGatewaySelect?: (gateway: "stripe" | "paypal") => void;
  onPaymentSuccess?: (paymentIntentId: string) => void;
  onError?: (error: string) => void;
}

export interface CheckoutStep {
  id: string;
  name: string;
  globalIndex: number; // Global index for sorting
  shouldBeUsed: (context: {
    isDigitalOnlyOrder: boolean;
    selectedPaymentMethod: string | null;
    step: number;
  }) => boolean;
  getStepNumber: (availableSteps: CheckoutStep[]) => number;
  render: (props: StepProps) => React.ReactElement | null;
}

export class CheckoutStepConfig {
  private steps: CheckoutStep[] = [];

  constructor() {
    // Step 1: Payment Method (always first, global index 1)
    this.steps.push({
      id: "payment-method",
      name: "Payment Method",
      globalIndex: 1,
      shouldBeUsed: () => true, // Always shown
      getStepNumber: (availableSteps) => {
        const index = availableSteps.findIndex((s) => s.id === "payment-method");
        return index + 1;
      },
      render: (props) => (
        <CheckoutPaymentMethod
          paymentMethods={props.paymentMethods || {}}
          isOnlinePaymentEnabled={props.isOnlinePaymentEnabled ?? false}
          isEmailVerified={props.isEmailVerified ?? false}
          selectedMethod={props.selectedMethod ?? null}
          onSelect={props.onSelect!}
          onNext={props.onNext!}
          onBack={props.onBack!}
          loading={props.loading ?? false}
          countryCode={props.countryCode}
          isDigitalOnlyOrder={props.isDigitalOnlyOrder ?? false}
          isStripeConfigured={props.isStripeConfigured ?? false}
          isPayPalConfigured={props.isPayPalConfigured ?? false}
        />
      ),
    });

    // Step 2: Billing Address (for digital-only orders, global index 2)
    this.steps.push({
      id: "billing-address",
      name: "Billing Address",
      globalIndex: 2,
      shouldBeUsed: (context) => context.isDigitalOnlyOrder,
      getStepNumber: (availableSteps) => {
        const index = availableSteps.findIndex((s) => s.id === "billing-address");
        return index >= 0 ? index + 1 : 2;
      },
      render: (props) => (
        <CheckoutShipping
          shippingAddress={props.billingAddress}
          billingAddress={props.billingAddress}
          useSameAddress={true}
          shippingRates={[]}
          selectedShipping={""}
          onShippingAddressChange={props.onBillingAddressChange!}
          onBillingAddressChange={props.onBillingAddressChange!}
          onUseSameAddressChange={() => {}}
          onShippingSelect={() => {}}
          onNext={props.onNext!}
          onBack={props.onBack!}
          errors={props.errors}
          onShippingAddressSelect={props.onBillingAddressSelect}
          onBillingAddressSelect={props.onBillingAddressSelect}
          selectedShippingAddressId={props.selectedBillingAddressId}
          selectedBillingAddressId={props.selectedBillingAddressId}
          isDigitalOnlyOrder={true}
        />
      ),
    });

    // Step 3: Shipping (for non-digital, non-pickup, global index 3)
    this.steps.push({
      id: "shipping",
      name: "Shipping",
      globalIndex: 3,
      shouldBeUsed: (context) =>
        !context.isDigitalOnlyOrder && context.selectedPaymentMethod !== "cash_on_pickup",
      getStepNumber: (availableSteps) => {
        const index = availableSteps.findIndex((s) => s.id === "shipping");
        return index >= 0 ? index + 1 : 2;
      },
      render: (props) => (
        <CheckoutShipping
          shippingAddress={props.shippingAddress || {
            firstName: "",
            lastName: "",
            address1: "",
            address2: "",
            city: "",
            province: "",
            postalCode: "",
            country: "",
            phone: "",
          }}
          billingAddress={props.billingAddress || {
            firstName: "",
            lastName: "",
            address1: "",
            address2: "",
            city: "",
            province: "",
            postalCode: "",
            country: "",
            phone: "",
          }}
          useSameAddress={props.useSameAddress ?? false}
          shippingRates={props.shippingRates || []}
          selectedShipping={props.selectedShipping || ""}
          onShippingAddressChange={props.onShippingAddressChange!}
          onBillingAddressChange={props.onBillingAddressChange!}
          onUseSameAddressChange={props.onUseSameAddressChange!}
          onShippingSelect={props.onShippingSelect!}
          onNext={props.onNext!}
          onBack={props.onBack!}
          errors={props.errors}
          onShippingAddressSelect={props.onShippingAddressSelect}
          onBillingAddressSelect={props.onBillingAddressSelect}
          selectedShippingAddressId={props.selectedShippingAddressId}
          selectedBillingAddressId={props.selectedBillingAddressId}
          isDigitalOnlyOrder={false}
          loading={props.loading ?? false}
        />
      ),
    });

    // Step 4: Review (always shown, global index 4)
    this.steps.push({
      id: "review",
      name: "Review",
      globalIndex: 4,
      shouldBeUsed: () => true, // Always shown
      getStepNumber: (availableSteps) => {
        const index = availableSteps.findIndex((s) => s.id === "review");
        return index >= 0 ? index + 1 : 3;
      },
      render: (props) => (
        <CheckoutReview
          shippingAddress={
            props.isDigitalOnlyOrder || props.selectedMethod === "cash_on_pickup"
              ? null
              : props.shippingAddress
          }
          billingAddress={props.selectedMethod === "cash_on_pickup" ? null : props.billingAddress || null}
          useSameAddress={props.useSameAddress ?? false}
          shippingMethod={
            props.isDigitalOnlyOrder || props.selectedMethod === "cash_on_pickup"
              ? null
              : props.getSelectedShippingMethod?.()
          }
          cartItems={props.cartItems!}
          subtotal={props.subtotal!}
          tax={props.tax!}
          shipping={props.shipping!}
          total={props.total!}
          currency={props.currency!}
          onBack={props.onBack!}
          onConfirm={props.onConfirm!}
          loading={props.loading}
          isOnlinePaymentEnabled={props.isOnlinePaymentEnabledReview}
          notes={props.notes}
          onNotesChange={props.onNotesChange}
        />
      ),
    });

    // Step 5: Payment (for online payment, global index 5)
    this.steps.push({
      id: "payment",
      name: "Payment",
      globalIndex: 5,
      shouldBeUsed: (context) => context.selectedPaymentMethod === "online_payment",
      getStepNumber: (availableSteps) => {
        const index = availableSteps.findIndex((s) => s.id === "payment");
        return index >= 0 ? index + 1 : 4;
      },
      render: (props) => (
        <CheckoutPayment
          clientSecret={props.clientSecret!}
          amount={props.total!}
          currency={props.currency!}
          stripePublishableKey={props.stripePublishableKey ?? null}
          isStripeConfigured={props.isStripeConfigured ?? false}
          paypalClientId={props.paypalClientId ?? null}
          isPayPalConfigured={props.isPayPalConfigured ?? false}
          selectedGateway={props.selectedGateway ?? null}
          onGatewaySelect={props.onGatewaySelect!}
          onPaymentSuccess={props.onPaymentSuccess!}
          onBack={props.onBack!}
          onError={props.onError!}
        />
      ),
    });

    // Step 6: Confirmation (always shown at the end, global index 6)
    // Note: This step is shown on a separate page, so render returns null
    this.steps.push({
      id: "confirmation",
      name: "Confirmation",
      globalIndex: 6,
      shouldBeUsed: () => true, // Always shown
      getStepNumber: (availableSteps) => {
        const index = availableSteps.findIndex((s) => s.id === "confirmation");
        return index >= 0 ? index + 1 : availableSteps.length;
      },
      render: () => null, // Confirmation is shown on separate page
    });
  }

  /**
   * Get all available steps based on context
   */
  getAvailableSteps(context: {
    isDigitalOnlyOrder: boolean;
    selectedPaymentMethod: string | null;
    step: number;
  }): CheckoutStep[] {
    // Filter steps based on shouldBeUsed
    const availableSteps = this.steps.filter((step) => step.shouldBeUsed(context));

    // Sort by global index
    return availableSteps.sort((a, b) => a.globalIndex - b.globalIndex);
  }

  /**
   * Get steps for the header display
   */
  getStepsForHeader(context: {
    isDigitalOnlyOrder: boolean;
    selectedPaymentMethod: string | null;
    step: number;
  }): Array<{ number: number; name: string }> {
    const availableSteps = this.getAvailableSteps(context);
    return availableSteps.map((step) => ({
      number: step.getStepNumber(availableSteps),
      name: step.name,
    }));
  }

  /**
   * Get the step component to render based on current step number
   */
  getStepComponent(
    stepNumber: number,
    context: {
      isDigitalOnlyOrder: boolean;
      selectedPaymentMethod: string | null;
      step: number;
    },
    props: StepProps
  ): React.ReactElement | null {
    const availableSteps = this.getAvailableSteps(context);
    
    if (stepNumber < 1 || stepNumber > availableSteps.length) {
      return null;
    }

    const step = availableSteps[stepNumber - 1];
    if (!step) {
      return null;
    }
    return step.render(props);
  }

  /**
   * Get the step ID from step number
   */
  getStepIdFromNumber(
    stepNumber: number,
    context: {
      isDigitalOnlyOrder: boolean;
      selectedPaymentMethod: string | null;
      step: number;
    }
  ): string | null {
    const availableSteps = this.getAvailableSteps(context);
    
    if (stepNumber < 1 || stepNumber > availableSteps.length) {
      return null;
    }

    return availableSteps[stepNumber - 1].id;
  }
}

// Export singleton instance
export const checkoutStepConfig = new CheckoutStepConfig();